/**
 * VAT Return Page (UI-7 v3 layout, BE-9 data)
 *
 * Period selector, VAT summary with warnings and credits, actions (file, FTA payment, refund), payments,
 * corrections already reported by Voluntary Disclosure, period history.
 * Route: /reports/vat-return
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Select, MenuItem, FormControl, InputLabel, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Alert, Skeleton, Tooltip, Link, ListSubheader, InputAdornment, Snackbar, Divider,
} from '@mui/material';
import { Gavel, Payment, Replay } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { StatusChip, CurrencyText, DataTable, JournalEntryLink, ReportExportMenu } from '../../components/accounting';
import { formatDate, formatCurrency } from '../../utils/accounting/formatters';
import { can, isTcAdmin } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import { vatReturnService } from '../../services/accounting/vatReturn.service';
import { creditCardService } from '../../services/accounting/creditCard.service';
import { cardLabel } from '../accounting/CreditCards';
import { serverMessage, unwrapList } from '../../utils/apiConvert';

const STATUS = {
  OPEN: { tone: 'info', label: 'Open' },
  FILED: { tone: 'warning', label: 'Filed — payment due' },
  PAID: { tone: 'success', label: 'Paid' },
  CARRIED: { tone: 'default', label: 'Credit carried' },
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const today = () => new Date().toISOString().slice(0, 10);
const twoDecimals = (v) => /^\d+(\.\d{1,2})?$/.test(String(v));
const EMPTY_PAY = { amount: '', paymentDate: today(), payFrom: '', reference: '' };

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

export const periodLabel = (p) => {
  if (!p) return '';
  const [sy, sm, sd] = p.startDate.split('-').map(Number);
  const [ey, em, ed] = p.endDate.split('-').map(Number);
  const lastDay = new Date(ey, em, 0).getDate();
  if (sd === 1 && ed === lastDay) {
    return sm === em && sy === ey ? `${MONTHS[sm - 1]} ${sy}` : `${MONTHS[sm - 1]}–${MONTHS[em - 1]} ${ey}`;
  }
  return `${sd} ${MONTHS[sm - 1]} – ${ed} ${MONTHS[em - 1]} ${ey}`;
};

const PeriodStatus = ({ status }) => <StatusChip tone={STATUS[status]?.tone || 'default'} label={STATUS[status]?.label || status} />;

const VatReturn = () => {
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [periods, setPeriods] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedStart, setSelectedStart] = useState('');
  const [vatData, setVatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(null);
  const [payForm, setPayForm] = useState(EMPTY_PAY);
  const [payFromOptions, setPayFromOptions] = useState([]);
  const [disclose, setDisclose] = useState(null);
  const [unfileReason, setUnfileReason] = useState(null);
  const [vdReference, setVdReference] = useState('');

  const loadPeriods = useCallback(async (keepSelection) => {
    try {
      const [list, s] = await Promise.all([vatReturnService.getTaxPeriods(), vatReturnService.getVatSettings().catch(() => null)]);
      setPeriods(list);
      setSettings(s);
      setBlocked(null);
      if (!keepSelection) {
        const firstDue = [...list].reverse().find((p) => p.status === 'OPEN' && p.endDate < today());
        setSelectedStart((firstDue || list[0])?.startDate || '');
      }
    } catch (err) {
      setBlocked(serverMessage(err, 'Failed to load tax periods'));
      setLoading(false);
    }
  }, []);

  const loadVatReturn = useCallback(async () => {
    if (!selectedStart) return;
    setLoading(true);
    try {
      setVatData(await vatReturnService.getVatReturn(selectedStart));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load VAT return'));
      setVatData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedStart]);

  useEffect(() => { loadPeriods(false); }, [loadPeriods]);
  useEffect(() => { loadVatReturn(); }, [loadVatReturn]);

  const refresh = async () => { await loadPeriods(true); await loadVatReturn(); };

  const selectedPeriod = periods.find((p) => p.startDate === selectedStart);
  const latestFiled = periods.find((p) => p.id);
  const remaining = selectedPeriod ? Math.max(0, Number(selectedPeriod.amountPayable || 0) - Number(selectedPeriod.amountPaid || 0)) : 0;
  const ended = selectedPeriod && selectedPeriod.endDate < today();
  const canFile = canWrite && selectedPeriod?.status === 'OPEN' && ended && settings?.periodsConfirmed;
  const canPay = canWrite && selectedPeriod?.status === 'FILED' && remaining > 0;
  // BE-31: the TC admin can unfile the latest filed period while nothing was paid or refunded on it
  const canUnfile = isTcAdmin(sessionUser().role) && Boolean(selectedPeriod?.canUnfile);
  const canRefund = canWrite && selectedPeriod?.id && selectedPeriod.id === latestFiled?.id && Number(selectedPeriod.creditCarriedForward || 0) > 0;
  const fileHint = !canWrite ? 'You have view access only'
    : selectedPeriod?.status !== 'OPEN' ? 'This period is already filed'
      : !ended ? `Can be filed after ${formatDate(selectedPeriod?.endDate)}`
        : !settings?.periodsConfirmed ? 'Save your VAT settings once to confirm the periods' : '';

  const run = async (fn, done) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setSnackbar(done);
      setFileOpen(false);
      setPayDialog(null);
      setDisclose(null);
      setUnfileReason(null);
      await refresh();
    } catch (err) {
      setError(serverMessage(err, 'The action failed'));
      setFileOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const openPay = async (kind) => {
    setPayForm({ ...EMPTY_PAY, amount: kind === 'PAYMENT' ? remaining.toFixed(2) : Number(selectedPeriod.creditCarriedForward).toFixed(2) });
    setPayDialog(kind);
    if (payFromOptions.length === 0) {
      try {
        const [banks, cards] = await Promise.all([
          ApiService.getTcBankAccounts().then((r) => unwrapList(r).items),
          creditCardService.getCards().catch(() => []),
        ]);
        setPayFromOptions([
          ...banks.filter((b) => b.isActive !== false && b.accountType === 'OPERATIONS')
            .map((b) => ({ value: `BANK:${b.id}`, label: [b.bankName, b.accountName].filter(Boolean).join(' - '), group: 'Bank accounts' })),
          ...cards.filter((c) => c.active !== false).map((c) => ({ value: `CARD:${c.id}`, label: cardLabel(c), group: 'TC credit cards' })),
        ]);
      } catch (err) {
        setError(serverMessage(err, 'Failed to load accounts'));
      }
    }
  };

  const submitPay = () => {
    const [kind, id] = payForm.payFrom.split(':');
    const data = {
      amount: payForm.amount, paymentDate: payForm.paymentDate, reference: payForm.reference.trim(),
      ...(kind === 'CARD' ? { cardId: id } : { bankAccountId: id }),
    };
    if (payDialog === 'PAYMENT') run(() => vatReturnService.recordPayment(selectedPeriod.id, data), 'FTA payment recorded');
    else run(() => vatReturnService.recordRefund(selectedPeriod.id, data), 'FTA refund recorded');
  };
  const payOptions = payFromOptions.filter((o) => payDialog === 'PAYMENT' || o.group === 'Bank accounts');
  const payLimit = payDialog === 'PAYMENT' ? remaining : Number(selectedPeriod?.creditCarriedForward || 0);
  const payValid = payForm.payFrom && Number(payForm.amount) > 0 && twoDecimals(payForm.amount) && Number(payForm.amount) <= payLimit
    && payForm.paymentDate && payForm.paymentDate <= today();

  const historyColumns = [
    { key: 'label', label: 'Period', width: '22%', render: (r) => periodLabel(r) },
    { key: 'outputVat', label: 'Output VAT', width: '13%', align: 'right', render: (r) => <CurrencyText amount={r.outputVat} /> },
    { key: 'inputVat', label: 'Input VAT', width: '13%', align: 'right', render: (r) => <CurrencyText amount={r.inputVat} /> },
    { key: 'netPayable', label: 'Net Payable', width: '13%', align: 'right', render: (r) => <CurrencyText amount={r.amountPayable} bold /> },
    { key: 'paid', label: 'Paid', width: '12%', align: 'right', render: (r) => <CurrencyText amount={r.amountPaid} /> },
    { key: 'status', label: 'Status', width: '15%', render: (r) => <PeriodStatus status={r.status} /> },
    { key: 'filedAt', label: 'Filed', width: '12%', render: (r) => (r.filedAt ? formatDate(r.filedAt) : '—') },
  ];

  const warnings = vatData?.warnings;
  const hasWarnings = selectedPeriod?.status === 'OPEN' && warnings
    && (warnings.uninvoicedWorkOrders > 0 || warnings.draftInvoices > 0 || warnings.message);
  const corrections = vatData?.corrections || [];
  const payments = selectedPeriod?.payments || [];
  const smallLine = (label, value) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.medium }}>{formatCurrency(value)}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 1 }}>VAT Return</Typography>

      {blocked ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {blocked} <Link component={RouterLink} to="/reports/vat-return?tab=settings">Open VAT settings</Link>
        </Alert>
      ) : (
        <>
          {settings && !settings.periodsConfirmed && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Save your VAT settings once to confirm the filing periods — filing stays blocked until then.{' '}
              <Link component={RouterLink} to="/reports/vat-return?tab=settings">Open VAT settings</Link>
            </Alert>
          )}

          {/* Period selector + status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel sx={{ fontSize: fs.sm }}>Tax Period</InputLabel>
              <Select value={selectedStart} label="Tax Period" onChange={(e) => setSelectedStart(e.target.value)} sx={{ fontSize: fs.sm }}>
                {periods.map((p) => (
                  <MenuItem key={p.startDate} value={p.startDate} sx={{ fontSize: fs.sm }}>{periodLabel(p)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedPeriod && <PeriodStatus status={selectedPeriod.status} />}
          </Box>

          {hasWarnings && (
            <Alert severity={warnings.message ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
              {warnings.message ? `${warnings.message} ` : ''}
              Not in this return yet: {warnings.uninvoicedWorkOrders || 0} work order{warnings.uninvoicedWorkOrders === 1 ? '' : 's'} not
              invoiced and {warnings.draftInvoices || 0} draft invoice{warnings.draftInvoices === 1 ? '' : 's'} — about{' '}
              {formatCurrency(warnings.estimatedVat)} of VAT. Finalise them before filing if they belong to this period.
            </Alert>
          )}

          {/* VAT Summary Card */}
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            {loading ? (
              <Box><Skeleton width="60%" height={32} /><Skeleton width="40%" /><Skeleton width="50%" /></Box>
            ) : vatData ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>Output VAT (Sales)</Typography>
                  <CurrencyText amount={vatData.supplies?.vat} size="lg" bold />
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>on {formatCurrency(vatData.supplies?.base)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>Input VAT (Purchases)</Typography>
                  <CurrencyText amount={vatData.expenses?.vat} size="lg" bold />
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>on {formatCurrency(vatData.expenses?.base)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>Net Payable to FTA</Typography>
                  <CurrencyText amount={vatData.amountPayable} size="lg" bold color="error" />
                  {selectedPeriod?.id && Number(selectedPeriod.amountPaid) > 0 && (
                    <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
                      paid {formatCurrency(selectedPeriod.amountPaid)}{remaining > 0 ? ` · ${formatCurrency(remaining)} still due` : ''}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {smallLine('Net VAT', vatData.netVat)}
                    {smallLine('Credit brought forward', vatData.creditBroughtForward)}
                    {smallLine('Credit carried forward', vatData.creditCarriedForward)}
                    <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
                      Transactions: {vatData.transactions?.outputCount || 0} output, {vatData.transactions?.inputCount || 0} input
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Typography sx={{ fontSize: fs.sm, color: 'text.secondary' }}>Select a tax period to view VAT return.</Typography>
            )}
          </Paper>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Tooltip title={canFile ? '' : fileHint}>
              <span>
                <Button variant="contained" size="small" startIcon={<Gavel />} onClick={() => setFileOpen(true)} disabled={!canFile || busy}
                  sx={{ textTransform: 'none', fontSize: fs.sm }}>File Period</Button>
              </span>
            </Tooltip>
            <Button variant="contained" size="small" color="success" startIcon={<Payment />} onClick={() => openPay('PAYMENT')}
              disabled={!canPay || busy} sx={{ textTransform: 'none', fontSize: fs.sm }}>Record Payment</Button>
            {canRefund && (
              <Button variant="outlined" size="small" startIcon={<Replay />} onClick={() => openPay('REFUND')} disabled={busy}
                sx={{ textTransform: 'none', fontSize: fs.sm }}>Record Refund</Button>
            )}
            {canUnfile && (
              <Button variant="outlined" size="small" color="warning" onClick={() => setUnfileReason('')} disabled={busy}
                sx={{ textTransform: 'none', fontSize: fs.sm }}>Unfile</Button>
            )}
            <span title="The selected period's return as PDF or CSV, for filing on EmaraTax by hand">
              <ReportExportMenu reportType="vat-return" params={{ from: selectedStart }} onError={setError} />
            </span>
          </Box>

          {/* Payments for the selected period */}
          {payments.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 0.75 }}>Payments and refunds</Typography>
              {payments.map((pm) => (
                <Box key={pm.id} sx={{ display: 'flex', gap: 2, py: 0.25, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: fs.xs, minWidth: 90 }}>{formatDate(pm.paymentDate)}</Typography>
                  <Typography sx={{ fontSize: fs.xs, minWidth: 70, color: pm.kind === 'REFUND' ? 'success.main' : 'text.primary' }}>
                    {pm.kind === 'REFUND' ? 'Refund' : 'Payment'}
                  </Typography>
                  <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, minWidth: 100 }}>{formatCurrency(pm.amount)}</Typography>
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>{pm.reference || ''}</Typography>
                  <JournalEntryLink id={pm.journalEntryId} />
                </Box>
              ))}
            </Paper>
          )}

          {/* Corrections to filed periods */}
          {corrections.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 0.5 }}>Corrections to filed periods</Typography>
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 1 }}>
                Changes posted after these periods were filed. They are included in this return unless already reported to
                the FTA by Voluntary Disclosure.
              </Typography>
              {corrections.map((c) => (
                <Box key={c.taxPeriodId} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: fs.xs, minWidth: 150 }}>{periodLabel(c)}</Typography>
                  <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, minWidth: 100 }}>{formatCurrency(c.vat)}</Typography>
                  {c.voluntaryDisclosureNeeded && <StatusChip tone="error" label="Voluntary Disclosure needed" />}
                  {canWrite && (
                    <Button size="small" sx={{ textTransform: 'none', fontSize: fs.xs }} onClick={() => { setVdReference(''); setDisclose(c); }}>
                      Mark as disclosed
                    </Button>
                  )}
                </Box>
              ))}
            </Paper>
          )}

          {/* Period History */}
          <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 1 }}>Period History</Typography>
          <DataTable columns={historyColumns} data={periods.map((p) => ({ ...p, id: p.id || p.startDate }))} loading={false}
            onRowClick={(row) => setSelectedStart(row.startDate)} emptyMessage="No tax periods found." pagination={false} />
        </>
      )}

      {/* File confirmation */}
      <Dialog open={fileOpen} onClose={() => setFileOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md }}>File {periodLabel(selectedPeriod)}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fs.sm, mb: 1 }}>
            Filing locks this period and posts the VAT return to the books. Later changes to this period go to the next
            return as corrections. If it was marked filed by mistake, the TC admin can unfile it until a payment is recorded.
          </Typography>
          <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>Net payable: {formatCurrency(vatData?.amountPayable)}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFileOpen(false)} size="small" sx={{ textTransform: 'none', fontSize: fs.sm }}>Cancel</Button>
          <Button variant="contained" size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: fs.sm }}
            onClick={() => run(() => vatReturnService.filePeriod(selectedStart), 'Period filed')}>File Period</Button>
        </DialogActions>
      </Dialog>

      {/* Unfile (BE-31) */}
      <Dialog open={unfileReason !== null} onClose={() => !busy && setUnfileReason(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md }}>Unfile {periodLabel(selectedPeriod)}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fs.sm, mb: 1.5 }}>
            The period goes back to open and its input VAT set-off is reversed today. No money moves. Use this only when
            the return was marked filed by mistake.
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={2} size="small" label="Reason" value={unfileReason || ''}
            onChange={(e) => setUnfileReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUnfileReason(null)} size="small" disabled={busy} sx={{ textTransform: 'none', fontSize: fs.sm }}>Cancel</Button>
          <Button variant="contained" color="warning" size="small" disabled={busy || !(unfileReason || '').trim()}
            sx={{ textTransform: 'none', fontSize: fs.sm }}
            onClick={() => run(() => vatReturnService.unfilePeriod(selectedPeriod.id, unfileReason.trim()), 'Period unfiled')}>
            Unfile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record FTA payment / refund */}
      <Dialog open={Boolean(payDialog)} onClose={() => setPayDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md }}>{payDialog === 'REFUND' ? 'Record FTA Refund' : 'Record FTA Payment'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <TextField label="Amount (AED)" size="small" fullWidth type="number" value={payForm.amount}
              onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
              helperText={`${payDialog === 'REFUND' ? 'Credit carried forward' : 'Still payable'}: ${formatCurrency(payLimit)}`}
              error={payForm.amount !== '' && (!twoDecimals(payForm.amount) || Number(payForm.amount) > payLimit)}
              InputProps={{ sx: { fontSize: fs.sm }, endAdornment: <InputAdornment position="end">AED</InputAdornment>, inputProps: { min: 0, step: '0.01' } }}
              InputLabelProps={{ sx: { fontSize: fs.sm } }} />
            <TextField label={payDialog === 'REFUND' ? 'Received On' : 'Payment Date'} type="date" size="small" fullWidth value={payForm.paymentDate}
              onChange={(e) => setPayForm({ ...payForm, paymentDate: e.target.value })} inputProps={{ max: today() }}
              InputLabelProps={{ shrink: true, sx: { fontSize: fs.sm } }} InputProps={{ sx: { fontSize: fs.sm } }} />
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: fs.sm }}>{payDialog === 'REFUND' ? 'Received Into' : 'Pay From'}</InputLabel>
              <Select value={payForm.payFrom} label={payDialog === 'REFUND' ? 'Received Into' : 'Pay From'} sx={{ fontSize: fs.sm }}
                onChange={(e) => setPayForm({ ...payForm, payFrom: e.target.value })}>
                {['Bank accounts', 'TC credit cards'].flatMap((g) => {
                  const opts = payOptions.filter((o) => o.group === g);
                  return opts.length ? [
                    <ListSubheader key={g} sx={{ lineHeight: '28px', fontSize: fs.xs }}>{g}</ListSubheader>,
                    ...opts.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: fs.sm }}>{o.label}</MenuItem>),
                  ] : [];
                })}
              </Select>
            </FormControl>
            <TextField label="FTA Reference" size="small" fullWidth value={payForm.reference}
              onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
              InputProps={{ sx: { fontSize: fs.sm } }} InputLabelProps={{ sx: { fontSize: fs.sm } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPayDialog(null)} size="small" sx={{ textTransform: 'none', fontSize: fs.sm }}>Cancel</Button>
          <Button variant="contained" size="small" onClick={submitPay} disabled={busy || !payValid} sx={{ textTransform: 'none', fontSize: fs.sm }}>
            {payDialog === 'REFUND' ? 'Record Refund' : 'Submit Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark corrections as disclosed */}
      <Dialog open={Boolean(disclose)} onClose={() => setDisclose(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md }}>Mark as Disclosed</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fs.sm, mb: 1.5 }}>
            {disclose && `${formatCurrency(disclose.vat)} for ${periodLabel(disclose)} was reported to the FTA by Voluntary Disclosure. It leaves this return.`}
          </Typography>
          <TextField label="Voluntary Disclosure reference" size="small" fullWidth value={vdReference}
            onChange={(e) => setVdReference(e.target.value)} InputProps={{ sx: { fontSize: fs.sm } }} InputLabelProps={{ sx: { fontSize: fs.sm } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisclose(null)} size="small" sx={{ textTransform: 'none', fontSize: fs.sm }}>Cancel</Button>
          <Button variant="contained" size="small" disabled={busy || !vdReference.trim()} sx={{ textTransform: 'none', fontSize: fs.sm }}
            onClick={() => run(() => vatReturnService.recordDisclosure({
              taxPeriodId: disclose.taxPeriodId, journalEntryIds: disclose.journalEntryIds, reference: vdReference.trim(),
            }), 'Correction marked as disclosed')}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default VatReturn;
